import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Users, ClipboardList, FileText, ShieldCheck, RefreshCw, Globe } from "lucide-react";

export default function SuperTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/tenants");
      setTenants(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!confirmTarget) return;
    if (confirmText !== confirmTarget.email) {
      toast.error("Type the tenant email to confirm");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/superadmin/tenants/${confirmTarget.id}`);
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purged ${data.tenant} · ${total} records removed`);
      setConfirmTarget(null);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="super-tenants-page" className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Globe className="w-6 h-6 text-rose-600" strokeWidth={1.5} /> Tenants
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            System-wide registry of every organisation on this instance. Only the bootstrap super-admin (you) can see this.
          </p>
        </div>
        <Button data-testid="super-tenants-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Organisation</th>
              <th className="text-left px-4 py-3 font-medium">Admin email</th>
              <th className="text-right px-4 py-3 font-medium">Team</th>
              <th className="text-right px-4 py-3 font-medium">Tasks</th>
              <th className="text-right px-4 py-3 font-medium">Obligations</th>
              <th className="text-left px-4 py-3 font-medium">Signed up</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && tenants.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No tenants.</td></tr>
            )}
            {!loading && tenants.map((t) => (
              <tr key={t.id} data-testid={`tenant-row-${t.email}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="px-4 py-3">
                  <div className="font-medium flex items-center gap-2">
                    {t.name || "—"}
                    {t.is_bootstrap && (
                      <span data-testid="badge-bootstrap" className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> System
                      </span>
                    )}
                    {!t.is_active && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">Inactive</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 mono text-slate-600">{t.email}</td>
                <td className="px-4 py-3 text-right tabular-nums"><span className="inline-flex items-center gap-1 text-slate-700"><Users className="w-3 h-3" /> {t.user_count}</span></td>
                <td className="px-4 py-3 text-right tabular-nums"><span className="inline-flex items-center gap-1 text-slate-700"><ClipboardList className="w-3 h-3" /> {t.task_count}</span></td>
                <td className="px-4 py-3 text-right tabular-nums"><span className="inline-flex items-center gap-1 text-slate-700"><FileText className="w-3 h-3" /> {t.obligation_count}</span></td>
                <td className="px-4 py-3 text-slate-500">{t.created_at?.slice(0, 10) || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {t.is_bootstrap ? (
                    <span className="text-xs text-slate-400 italic">protected</span>
                  ) : (
                    <Button
                      data-testid={`tenant-delete-${t.email}`}
                      variant="outline"
                      size="sm"
                      className="text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => { setConfirmTarget(t); setConfirmText(""); }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Purge
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!confirmTarget} onOpenChange={(o) => { if (!o) { setConfirmTarget(null); setConfirmText(""); } }}>
        <DialogContent data-testid="tenant-purge-dialog">
          <DialogHeader>
            <DialogTitle>Purge tenant — this cannot be undone</DialogTitle>
            <DialogDescription>
              About to permanently delete <span className="font-medium">{confirmTarget?.name}</span> ({confirmTarget?.email}),
              their {confirmTarget?.user_count} team member(s), {confirmTarget?.task_count} task(s), and {confirmTarget?.obligation_count} obligation(s).
              <br />
              Type the tenant email to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              data-testid="tenant-purge-confirm-input"
              placeholder={confirmTarget?.email}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmTarget(null); setConfirmText(""); }}>Cancel</Button>
            <Button
              data-testid="tenant-purge-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={deleting || confirmText !== confirmTarget?.email}
              onClick={confirmDelete}
            >
              {deleting ? "Purging…" : "Purge tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
